import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/CustomToast';
import PatientPersonaForm from './PatientPersonaForm';
import {
  Stethoscope, Plus, Users, Loader2, Search,
  Languages, Brain, Frown, Smile, Meh, AlertTriangle
} from 'lucide-react';
import './PatientLibrary.css';

const MOOD_ICONS = {
  cooperative: <Smile className="w-3 h-3" />,
  anxious: <AlertTriangle className="w-3 h-3" />,
  dismissive: <Frown className="w-3 h-3" />,
  verbose: <Meh className="w-3 h-3" />,
};

const MOOD_COLORS = {
  cooperative: { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0' },
  anxious: { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  dismissive: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  verbose: { bg: '#ede9fe', color: '#7c3aed', border: '#c4b5fd' },
};

const DIFFICULTY_COLORS = {
  Beginner: { bg: '#ecfdf5', color: '#059669' },
  Intermediate: { bg: '#fef3c7', color: '#b45309' },
  Advanced: { bg: '#fef2f2', color: '#dc2626' },
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #7c3aed, #a78bfa)',
  'linear-gradient(135deg, #059669, #34d399)',
  'linear-gradient(135deg, #2563eb, #60a5fa)',
  'linear-gradient(135deg, #db2777, #f472b6)',
  'linear-gradient(135deg, #d97706, #fbbf24)',
  'linear-gradient(135deg, #7c3aed, #ec4899)',
];

export default function PatientLibrary() {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [casesLoading, setCasesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);

  // ── Load ClinicalSim accounts ──
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${apiUrl}/api/v1/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const csAccounts = (res.data?.users || []).filter(
          (u) => String(u.dashboard_type || '').toLowerCase() === 'clinicalsim'
        );
        setAccounts(csAccounts);
        if (csAccounts.length > 0) setSelectedAccountId(csAccounts[0].id);
      } catch (err) {
        console.error('Failed to load accounts', err);
        toast.error('Failed to load ClinicalSim accounts');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // ── Load cases for selected account ──
  const loadCases = useCallback(async () => {
    if (!token || !selectedAccountId) { setCases([]); return; }
    try {
      setCasesLoading(true);
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const casesRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/cases?client_id=${selectedAccountId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCases(casesRes.data?.cases || []);
    } catch (err) {
      console.error('Failed to load cases', err);
    } finally {
      setCasesLoading(false);
    }
  }, [token, selectedAccountId]);

  useEffect(() => { loadCases(); }, [loadCases]);

  const filteredCases = search
    ? cases.filter(
        (c) =>
          c.title?.toLowerCase().includes(search.toLowerCase()) ||
          c.patient?.name?.toLowerCase().includes(search.toLowerCase()) ||
          c.category?.toLowerCase().includes(search.toLowerCase())
      )
    : cases;

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  // ── Render ──
  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-slate-50">
        
        <div className="flex-1 flex items-center justify-center h-screen ">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      
      <div className="pl-page " style={{ flex: 1 }}>
        {/* ── Top Bar ── */}
        <div className="pl-topbar">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-sm">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1>Patient Library</h1>
              <p className="text-xs text-slate-400 font-medium">
                Manage AI patient personas for ClinicalSim training
              </p>
            </div>
          </div>

          <div className="pl-topbar-right">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search personas…"
                className="pl-3 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-52 focus:outline-none focus:border-purple-400"
                style={{ paddingLeft: 32 }}
              />
            </div>

            {/* Account Selector */}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <select
                className="pl-account-select"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.length === 0 && (
                  <option value="">No ClinicalSim accounts</option>
                )}
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name || acc.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Create Button */}
            <button
              className="pl-create-btn"
              disabled={!selectedAccountId}
              onClick={() => { setEditingCase(null); setFormOpen(true); }}
            >
              <Plus className="w-4 h-4" /> Create Patient
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        {casesLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="pl-empty">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-2">
              <Stethoscope className="w-8 h-8 text-purple-300" />
            </div>
            <h3>{cases.length === 0 ? 'No patient personas yet' : 'No results'}</h3>
            <p>
              {cases.length === 0
                ? `Create your first AI patient persona for ${selectedAccount?.name || 'this account'}.`
                : 'Try adjusting your search terms.'}
            </p>
          </div>
        ) : (
          <div className="pl-grid">
            {filteredCases.map((c, idx) => {
              const moodKey = (c.patient?.mood || 'cooperative').toLowerCase();
              const moodStyle = MOOD_COLORS[moodKey] || MOOD_COLORS.cooperative;
              const diffStyle = DIFFICULTY_COLORS[c.difficulty] || DIFFICULTY_COLORS.Beginner;

              return (
                <div key={c.case_id} className="pl-card">
                  <div className="pl-card-header">
                    <div
                      className="pl-card-avatar"
                      style={{ background: AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length] }}
                    >
                      {c.patient?.initials || c.patient?.name?.charAt(0) || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3>{c.patient?.name || 'Unknown Patient'}</h3>
                      <p>
                        {c.patient?.age ? `${c.patient.age}y` : ''}{' '}
                        {c.patient?.gender ? `• ${c.patient.gender}` : ''}{' '}
                        {c.category ? `• ${c.category}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="pl-card-body">
                    <div className="pl-tag-row">
                      {/* Difficulty */}
                      <span
                        className="pl-tag"
                        style={{ background: diffStyle.bg, color: diffStyle.color }}
                      >
                        {c.difficulty || 'Beginner'}
                      </span>
                      {/* Mood */}
                      <span
                        className="pl-tag"
                        style={{
                          background: moodStyle.bg,
                          color: moodStyle.color,
                          border: `1px solid ${moodStyle.border}`,
                        }}
                      >
                        {MOOD_ICONS[moodKey]} {moodKey}
                      </span>
                      {/* Language */}
                      {c.patient?.language && (
                        <span className="pl-tag" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                          <Languages className="w-3 h-3" /> {c.patient.language}
                        </span>
                      )}
                      {/* Emergency */}
                      {c.is_emergency && (
                        <span className="pl-tag" style={{ background: '#fef2f2', color: '#dc2626' }}>
                          🚨 Emergency
                        </span>
                      )}
                    </div>
                    <p className="pl-card-complaint">
                      {c.chief_complaint || 'No chief complaint specified'}
                    </p>
                  </div>

                  <div className="pl-card-footer">
                    <span className="text-xs text-slate-400 font-mono">{c.case_id}</span>
                    <button onClick={() => { setEditingCase(c); setFormOpen(true); }}>
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Persona Builder Slide-Over ── */}
      {formOpen && (
        <PatientPersonaForm
          accountId={selectedAccountId}
          accountName={selectedAccount?.name || selectedAccount?.email || ''}
          existingCase={editingCase}
          onClose={() => { setFormOpen(false); setEditingCase(null); }}
          onSaved={() => {
            setFormOpen(false);
            setEditingCase(null);
            loadCases();
          }}
        />
      )}
    </div>
  );
}
